<template>
  <div class="page-container animate-fade-in-up">
    <PageHeader icon="el-icon-user" title="评估对象管理" :subtitle="filterStatus === 'active' ? '管理当前在职的评估对象，支持批量导入、编辑、导出' : '管理已离职员工，支持直接添加离职员工信息'" />

    <div class="toolbar">
      <el-input v-model="search" placeholder="搜索姓名、邮箱、部门、职位，支持拼音首字母" prefix-icon="el-icon-search" clearable style="width: 320px;" />
      <el-select v-model="filterDept" placeholder="筛选部门" clearable style="width: 150px; margin-left: 12px;">
        <el-option v-for="d in departments" :key="d" :label="d" :value="d" />
      </el-select>
      <el-button v-if="filterDept || search" type="text" style="margin-left: 12px; color: var(--color-primary);" @click="clearFilters">清除筛选</el-button>
      <el-radio-group v-model="filterStatus" size="small" style="margin-left: 12px;">
        <el-radio-button label="active">在职</el-radio-button>
        <el-radio-button label="resigned">已离职</el-radio-button>
      </el-radio-group>
      <div style="margin-left: auto; display: flex; gap: 12px;">
        <el-button icon="el-icon-download" class="btn-click" @click="exportEmployees">导出评估对象</el-button>
        <template v-if="filterStatus === 'active'">
          <el-button v-if="$hasPermission('employee:edit')" type="primary" icon="el-icon-plus" class="btn-click" @click="openAdd">手动添加</el-button>
          <el-button v-if="$hasPermission('employee:import')" icon="el-icon-upload2" class="btn-click" @click="openImport">批量导入评估对象</el-button>
          <el-button v-if="isSuperAdmin" icon="el-icon-refresh-left" class="btn-click" @click="openResetPassword">重置主管密码</el-button>
        </template>
        <template v-else>
          <el-button type="primary" icon="el-icon-plus" class="btn-click" @click="openAddResignedDialog">添加离职员工</el-button>
        </template>
      </div>
    </div>

    <div class="table-wrapper">
      <el-table
        ref="tableRef"
        :data="pagedList"
        stripe
        highlight-current-row
        class="data-table"
        @selection-change="handleSelectionChange"
      >
        <el-table-column v-if="$hasPermission('employee:edit') || $hasPermission('employee:resign') || $hasPermission('employee:delete')" type="selection" width="45" />
        <el-table-column label="姓名" min-width="100">
          <template slot-scope="scope">
            <span style="display: inline-flex; align-items: center; gap: 6px;">
              {{ scope.row.real_name }}
              <el-tag v-if="isSupervisor(scope.row)" size="mini" type="warning" effect="plain" style="height: 18px; line-height: 16px; padding: 0 4px;">主管</el-tag>
            </span>
          </template>
        </el-table-column>
        <el-table-column prop="email" label="邮箱" min-width="170" />
        <el-table-column prop="dept_name" label="部门" min-width="100">
          <template slot-scope="scope">
            <span>{{ scope.row.dept_name || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column prop="job_title" label="职位" min-width="110">
          <template slot-scope="scope">
            <span>{{ scope.row.job_title || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column v-if="filterStatus === 'resigned'" label="离职日期" min-width="110">
          <template slot-scope="scope">
            <span>{{ scope.row.resignation_date || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column v-if="filterStatus === 'resigned'" label="离职原因" min-width="150" show-overflow-tooltip>
          <template slot-scope="scope">
            <span>{{ scope.row.resignation_reason || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column label="上级" min-width="90">
          <template slot-scope="scope">
            <span>{{ scope.row.manager_name || getManagerName(scope.row.manager_email) || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column label="上级邮箱" min-width="170">
          <template slot-scope="scope">
            <span style="font-size: 12px; color: var(--color-text-secondary);">{{ scope.row.manager_email || '-' }}</span>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="210">
          <template slot-scope="scope">
            <!-- 在职 -->
            <template v-if="scope.row.employment_status === 1">
              <el-button v-if="$hasPermission('employee:edit')" type="text" class="action-edit" @click="openEdit(scope.row)">编辑</el-button>
              <el-button v-if="$hasPermission('employee:resign')" type="text" style="color: var(--color-medium);" @click="openResignDialog(scope.row)">标记离职</el-button>
              <el-dropdown v-if="$hasPermission('employee:delete')" style="margin-left: 8px;">
                <el-button type="text" class="action-disable">更多</el-button>
                <el-dropdown-menu slot="dropdown">
                  <el-dropdown-item :disabled="hasEvalRecord(scope.row)" @click.native="!hasEvalRecord(scope.row) && remove(scope.row)">
                    <span :style="{ color: hasEvalRecord(scope.row) ? 'var(--color-text-muted)' : 'var(--color-high)' }">
                      {{ hasEvalRecord(scope.row) ? '已评估（不可删）' : '删除' }}
                    </span>
                  </el-dropdown-item>
                </el-dropdown-menu>
              </el-dropdown>
            </template>
            <!-- 离职 -->
            <template v-else>
              <el-button v-if="$hasPermission('employee:edit')" type="text" class="action-edit" @click="openEdit(scope.row)">编辑</el-button>
              <el-button v-if="$hasPermission('employee:edit')" type="text" style="color: var(--color-primary);" @click="restoreEmployee(scope.row)">恢复在职</el-button>
              <el-dropdown v-if="$hasPermission('employee:delete')" style="margin-left: 8px;">
                <el-button type="text" class="action-disable">更多</el-button>
                <el-dropdown-menu slot="dropdown">
                  <el-dropdown-item @click.native="remove(scope.row)">
                    <span style="color: var(--color-high);">删除</span>
                  </el-dropdown-item>
                </el-dropdown-menu>
              </el-dropdown>
            </template>
          </template>
        </el-table-column>
      </el-table>
      <!-- 批量操作栏 -->
      <div v-if="selectedRows.length && ($hasPermission('employee:resign') || $hasPermission('employee:delete'))" class="batch-bar">
        <span class="batch-info">已选择 {{ selectedRows.length }} 人</span>
        <div class="batch-actions">
          <el-button v-if="$hasPermission('employee:resign') && filterStatus === 'active'" size="small" icon="el-icon-user-solid" @click="batchResign">批量标记离职</el-button>
          <el-button v-if="$hasPermission('employee:delete')" size="small" type="danger" icon="el-icon-delete" @click="batchDelete">批量删除</el-button>
          <el-button size="small" @click="clearSelection">取消选择</el-button>
        </div>
      </div>
      <div class="pagination-bar">
        <el-pagination
          background
          layout="total, sizes, prev, pager, next"
          :total="filteredList.length"
          :page-size="pageSize"
          :current-page="page"
          :page-sizes="[10, 20, 50]"
          @size-change="handleSizeChange"
          @current-change="handlePageChange"
        />
      </div>
    </div>

    <!-- 新增/编辑弹窗 -->
    <el-dialog :title="dialogTitle" :visible.sync="dialogVisible" width="520px" :append-to-body="true">
      <el-form :model="form" :rules="formRules" ref="formRef" label-width="90px">
        <el-form-item label="姓名" prop="real_name">
          <el-input v-model="form.real_name" placeholder="请输入姓名" />
        </el-form-item>
        <el-form-item label="邮箱" prop="email">
          <el-input v-model="form.email" placeholder="请输入邮箱" :disabled="isEdit" />
        </el-form-item>
        <el-form-item label="部门">
          <el-input v-model="form.dept_name" placeholder="请输入部门（可选）" />
        </el-form-item>
        <el-form-item label="职位">
          <el-input v-model="form.job_title" placeholder="请输入职位（可选）" />
        </el-form-item>
        <el-form-item label="汇报上级" prop="manager_email">
          <el-input v-model="form.manager_email" placeholder="请输入汇报上级的邮箱" />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="saving" @click="save">保存</el-button>
      </span>
    </el-dialog>

    <!-- 标记离职弹窗 -->
    <el-dialog title="标记离职" :visible.sync="resignVisible" width="560px" :append-to-body="true">
      <el-form label-width="100px">
        <el-form-item label="员工">
          <span style="font-weight: 600;">{{ resignForm.employee_name }}</span>
        </el-form-item>
        <el-form-item label="离职日期">
          <el-date-picker v-model="resignForm.resignation_date" type="date" placeholder="选择离职日期" value-format="yyyy-MM-dd" style="width: 100%;" />
        </el-form-item>
        <el-form-item label="离职原因">
          <el-checkbox-group v-model="resignForm.resignation_reasons">
            <el-checkbox label="薪酬">薪酬</el-checkbox>
            <el-checkbox label="发展空间">发展空间</el-checkbox>
            <el-checkbox label="工作压力">工作压力</el-checkbox>
            <el-checkbox label="家庭原因">家庭原因</el-checkbox>
            <el-checkbox label="其他">其他</el-checkbox>
          </el-checkbox-group>
          <el-input v-if="resignForm.resignation_reasons.includes('其他')" v-model="resignForm.other_reason" placeholder="请说明其他原因" size="small" style="margin-top: 8px;" />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="resignVisible = false">取消</el-button>
        <el-button type="primary" :loading="saving" @click="confirmResign">确认标记离职</el-button>
      </span>
    </el-dialog>

    <!-- 添加离职员工弹窗 -->
    <el-dialog title="添加离职员工" :visible.sync="addResignedDialogVisible" width="520px" :append-to-body="true">
      <el-form :model="resignedForm" :rules="resignedFormRules" ref="resignedFormRef" label-width="100px">
        <el-form-item label="姓名" prop="real_name">
          <el-input v-model="resignedForm.real_name" placeholder="请输入姓名" />
        </el-form-item>
        <el-form-item label="邮箱" prop="email">
          <el-input v-model="resignedForm.email" placeholder="请输入邮箱" />
        </el-form-item>
        <el-form-item label="部门">
          <el-input v-model="resignedForm.dept_name" placeholder="请输入部门（可选）" />
        </el-form-item>
        <el-form-item label="职位">
          <el-input v-model="resignedForm.job_title" placeholder="请输入职位（可选）" />
        </el-form-item>
        <el-form-item label="离职日期" prop="resignation_date">
          <el-date-picker v-model="resignedForm.resignation_date" type="date" placeholder="选择离职日期" value-format="yyyy-MM-dd" style="width: 100%;" />
        </el-form-item>
        <el-form-item label="离职原因">
          <el-input v-model="resignedForm.resignation_reason" type="textarea" :rows="2" placeholder="请输入离职原因（可选）" />
        </el-form-item>
      </el-form>
      <span slot="footer">
        <el-button @click="addResignedDialogVisible = false">取消</el-button>
        <el-button type="primary" :loading="saving" @click="confirmAddResigned">保存</el-button>
      </span>
    </el-dialog>

    <!-- Excel导入弹窗 -->
    <el-dialog title="Excel批量导入" :visible.sync="importVisible" width="640px" :append-to-body="true">
      <div class="import-area">
        <div class="import-header">
          <p class="import-tip">
            <i class="el-icon-info" style="color: var(--color-primary);"></i>
            请上传 .xlsx 或 .xls 文件，需包含以下列：<b>姓名*、邮箱*、部门、职位、上级邮箱</b>
          </p>
          <el-button type="text" icon="el-icon-download" @click="downloadTemplate">下载导入模板</el-button>
        </div>
        <el-upload
          ref="uploadRef"
          action=""
          :auto-upload="false"
          :on-change="handleFileChange"
          :limit="1"
          accept=".xlsx,.xls"
          drag
          class="upload-box"
        >
          <i class="el-icon-upload"></i>
          <div class="el-upload__text">将文件拖到此处，或<em>点击上传</em></div>
        </el-upload>

        <!-- 预览区域 -->
        <div v-if="previewRows.length" class="import-preview">
          <el-divider></el-divider>
          <div class="preview-header">
            <p><b>数据预览：</b>共 {{ previewRows.length }} 行，有效 {{ validPreviewCount }} 行，异常 {{ invalidPreviewCount }} 行</p>
            <div v-if="duplicateCount > 0" class="duplicate-strategy">
              <span style="font-size: 13px; color: var(--color-text-secondary);">发现 {{ duplicateCount }} 条重复邮箱：</span>
              <el-radio-group v-model="importStrategy" size="mini">
                <el-radio label="skip">跳过重复</el-radio>
                <el-radio label="overwrite">覆盖更新</el-radio>
              </el-radio-group>
            </div>
          </div>
          <el-table :data="previewRows" size="small" max-height="300" class="preview-table">
            <el-table-column type="index" label="行号" width="60" />
            <el-table-column prop="real_name" label="姓名" width="90">
              <template slot-scope="s">
                <span :class="{ 'text-error': s.row.errors.includes('name') }">{{ s.row.real_name || '-' }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="email" label="邮箱" width="170">
              <template slot-scope="s">
                <span :class="{ 'text-error': s.row.errors.includes('email') || s.row.errors.includes('duplicate') }">{{ s.row.email || '-' }}</span>
              </template>
            </el-table-column>
            <el-table-column prop="dept_name" label="部门" width="100" />
            <el-table-column prop="job_title" label="职位" width="100" />
            <el-table-column prop="manager_email" label="上级邮箱" width="170" />
            <el-table-column label="状态" width="80">
              <template slot-scope="s">
                <el-tag v-if="s.row.errors.length === 0" size="mini" type="success">正常</el-tag>
                <el-tag v-else size="mini" type="danger">异常</el-tag>
              </template>
            </el-table-column>
          </el-table>
          <div v-if="previewErrors.length" class="preview-errors">
            <p style="color: var(--color-high); font-size: 13px; margin: 8px 0;"><b>异常明细：</b></p>
            <ul>
              <li v-for="(err, idx) in previewErrors" :key="idx">{{ err }}</li>
            </ul>
          </div>
        </div>
      </div>
      <span slot="footer">
        <el-button @click="importVisible = false">关闭</el-button>
        <el-button type="primary" :loading="importing" :disabled="!canConfirmImport || importing" @click="confirmImport">
          确认导入（{{ validPreviewCount }} 条）
        </el-button>
      </span>
    </el-dialog>

    <!-- 重置密码弹窗 -->
    <el-dialog title="重置主管密码" :visible.sync="resetPwdVisible" width="480px" :append-to-body="true">
      <div v-if="resetPwdList.length">
        <p style="margin: 0 0 12px 0; color: var(--color-text-secondary); font-size: 14px;">以下主管的密码将重置为其邮箱地址：</p>
        <ul style="margin: 0 0 16px 0; padding-left: 18px; line-height: 1.8;">
          <li v-for="emp in resetPwdList.slice(0, 3)" :key="emp.email">
            <span style="font-weight: 600;">{{ emp.real_name }}</span>（{{ emp.email }}）
            <span style="color: var(--color-primary); margin-left: 8px;">密码：{{ emp.email }}</span>
            <el-button type="text" size="mini" style="margin-left: 4px;" @click="copyText(emp.email)">复制</el-button>
          </li>
          <li v-if="resetPwdList.length > 3" style="color: var(--color-text-muted);">等 {{ resetPwdList.length }} 人</li>
        </ul>
        <p style="margin: 0; font-size: 13px; color: var(--color-text-muted);">
          <i class="el-icon-info" style="margin-right: 4px;"></i>
          主管首次登录后需强制修改密码。请通过企业微信或邮件将密码发送给对应主管。
        </p>
      </div>
      <span slot="footer">
        <el-button @click="resetPwdVisible = false">取消</el-button>
        <el-button type="primary" @click="confirmResetPassword">确认重置</el-button>
      </span>
    </el-dialog>

    <!-- 禁用账号弹窗 -->
    <el-dialog title="禁用主管账号" :visible.sync="disableVisible" width="480px" :append-to-body="true">
      <div v-if="disableList.length">
        <p style="margin: 0 0 12px 0; color: var(--color-text-secondary); font-size: 14px;">禁用后，以下主管将无法登录系统，但其历史数据完整保留。</p>
        <ul style="margin: 0 0 16px 0; padding-left: 18px; line-height: 1.8;">
          <li v-for="emp in disableList" :key="emp.employee_id">
            <span style="font-weight: 600;">{{ emp.real_name }}</span>（{{ emp.email }}）
            <span v-if="getSubordinateCount(emp.email) > 0" style="color: var(--color-high); margin-left: 8px; font-size: 12px;">
              有 {{ getSubordinateCount(emp.email) }} 名下属
            </span>
          </li>
        </ul>
        <div v-if="disableList.some(e => getSubordinateCount(e.email) > 0)" style="padding: 10px 12px; background: #fef0f0; border-radius: 6px; font-size: 13px; color: var(--color-high);">
          <i class="el-icon-warning" style="margin-right: 4px;"></i>
          注意：部分主管有下属，禁用后其下属将变为无上级，请及时在员工管理中重新分配。
        </div>
      </div>
      <span slot="footer">
        <el-button @click="disableVisible = false">取消</el-button>
        <el-button type="primary" @click="confirmDisable">确认禁用</el-button>
      </span>
    </el-dialog>

    <!-- 启用账号弹窗 -->
    <el-dialog title="启用主管账号" :visible.sync="enableVisible" width="480px" :append-to-body="true">
      <div v-if="enableList.length">
        <p style="margin: 0 0 12px 0; color: var(--color-text-secondary); font-size: 14px;">恢复以下主管的登录权限。</p>
        <ul style="margin: 0; padding-left: 18px; line-height: 1.8;">
          <li v-for="emp in enableList" :key="emp.employee_id">
            <span style="font-weight: 600;">{{ emp.real_name }}</span>（{{ emp.email }}）
          </li>
        </ul>
      </div>
      <span slot="footer">
        <el-button @click="enableVisible = false">取消</el-button>
        <el-button type="primary" @click="confirmEnable">确认启用</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import PageHeader from '../../components/PageHeader.vue'
import RiskScoreTag from '../../components/RiskScoreTag.vue'
import { filterByPinyin } from '../../utils/pinyinFilter'
import { filterByDataScope } from '../../utils/permission'
import * as XLSX from 'xlsx'

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export default {
  name: 'EmployeeManage',
  components: { PageHeader, RiskScoreTag },
  data() {
    return {
      search: '',
      filterDept: sessionStorage.getItem('em_filterDept') || '',
      dialogVisible: false,
      isEdit: false,
      form: {
        employee_id: null,
        real_name: '',
        email: '',
        dept_name: '',
        job_title: '',
        manager_email: ''
      },
      // 分页
      page: 1,
      pageSize: 10,
      // 批量操作
      selectedRows: [],
      // 标记离职弹窗
      resignVisible: false,
      resignForm: {
        employee_id: null,
        employee_name: '',
        resignation_date: new Date().toISOString().slice(0, 10),
        resignation_reasons: [],
        other_reason: ''
      },
      formRules: {
        real_name: [{ required: true, message: '请输入姓名', trigger: 'blur' }],
        email: [
          { required: true, message: '请输入邮箱', trigger: 'blur' },
          { validator: (rule, value, cb) => {
            if (value && !EMAIL_REGEX.test(value)) {
              cb(new Error('邮箱格式不正确'))
            } else {
              cb()
            }
          }, trigger: 'blur' }
        ],
        manager_email: [
          { required: true, message: '请输入汇报上级邮箱', trigger: 'blur' },
          { validator: (rule, value, cb) => {
            if (value && !EMAIL_REGEX.test(value)) {
              cb(new Error('邮箱格式不正确'))
            } else {
              cb()
            }
          }, trigger: 'blur' }
        ]
      },
      // 主管账号数据
      managers: [],
      // 主管账号操作弹窗
      resetPwdVisible: false,
      resetPwdList: [],
      disableVisible: false,
      disableList: [],
      enableVisible: false,
      enableList: [],
      importVisible: false,
      importFile: null,
      importResult: { summary: '', errors: [], success: 0, fail: 0 },
      // 导入预览状态
      previewRows: [],
      previewErrors: [],
      importStrategy: 'skip', // skip | overwrite
      filterStatus: 'active',
      searchDebounced: '',
      searchTimer: null,
      saving: false,
      importing: false,
      // 添加离职员工弹窗
      addResignedDialogVisible: false,
      resignedForm: {
        real_name: '',
        email: '',
        dept_name: '',
        job_title: '',
        resignation_date: new Date().toISOString().slice(0, 10),
        resignation_reason: ''
      },
      resignedFormRules: {
        real_name: [{ required: true, message: '请输入姓名', trigger: 'blur' }],
        email: [
          { required: true, message: '请输入邮箱', trigger: 'blur' },
          { validator: (rule, value, cb) => {
            if (value && !EMAIL_REGEX.test(value)) {
              cb(new Error('邮箱格式不正确'))
            } else {
              cb()
            }
          }, trigger: 'blur' }
        ],
        resignation_date: [{ required: true, message: '请选择离职日期', trigger: 'change' }]
      }
    }
  },
  mounted() {
    // 加载主管列表（用于显示主管状态）
    this.loadManagers()
    // 处理从评估记录页跳转过来的 highlight 参数：自动搜索并定位到目标员工
    const highlightId = this.$route.query.highlight
    if (highlightId) {
      const id = parseInt(highlightId, 10)
      const emp = this.allList.find(e => e.employee_id === id)
      if (emp) {
        this.search = emp.real_name
        this.$nextTick(() => {
          const row = this.pagedList.find(e => e.employee_id === id)
          if (row && this.$refs.tableRef) {
            this.$refs.tableRef.setCurrentRow(row)
            // 高亮 3 秒后清除
            setTimeout(() => {
              this.$refs.tableRef.setCurrentRow(null)
            }, 3000)
          }
        })
      }
      // 清除 query，防止刷新后重复触发
      this.$router.replace({ path: this.$route.path, query: {} })
    }
  },
  watch: {
    filterDept(val) {
      sessionStorage.setItem('em_filterDept', val || '')
      this.page = 1
    },
    search(val) {
      clearTimeout(this.searchTimer)
      this.searchTimer = setTimeout(() => {
        this.searchDebounced = val
        this.page = 1
      }, 300)
    },
    filterStatus() {
      this.page = 1
    }
  },
  computed: {
    allList() {
      const taskId = this.$store.getters.currentTaskId
      const all = [
        ...this.$store.state.employees.employees,
        ...this.$store.state.employees.resignedEmployees
      ]
      if (!taskId) return all
      return all.filter(e => e.task_id === taskId || !e.task_id)
    },
    isSuperAdmin() {
      const user = this.$store.state.user
      return user && user.role === 'super_admin'
    },
    departments() {
      const depts = this.allList.map(e => e.dept_name).filter(Boolean)
      return [...new Set(depts)]
    },
    filteredList() {
      let data = [...this.allList]
      // 根据状态筛选
      if (this.filterStatus === 'active') {
        data = data.filter(e => e.employment_status === 1)
      } else if (this.filterStatus === 'resigned') {
        data = data.filter(e => e.employment_status === 2)
      }
      if (this.searchDebounced) {
        const k = this.searchDebounced.toLowerCase()
        // 优先使用拼音首字母搜索
        data = filterByPinyin(data, this.searchDebounced)
        // 如果拼音搜索未命中，fallback 到常规搜索
        if (data.length === 0 || this.searchDebounced.length <= 1) {
          data = this.allList.filter(e =>
            e.real_name.includes(k) ||
            (e.email && e.email.toLowerCase().includes(k)) ||
            (e.dept_name && e.dept_name.includes(k)) ||
            (e.job_title && e.job_title.includes(k))
          )
        }
      }
      if (this.filterDept) data = data.filter(e => e.dept_name === this.filterDept)
      return filterByDataScope(this.$store, data)
    },
    pagedList() {
      const start = (this.page - 1) * this.pageSize
      return this.filteredList.slice(start, start + this.pageSize)
    },
    dialogTitle() { return this.isEdit ? '编辑评估对象' : '新增评估对象' },
    managerOptions() {
      // 汇报上级候选列表：排除自己
      return this.allList.filter(e => e.email !== this.form.email)
    },
    activeBehaviors() {
      return this.$store.getters.taskBehaviors || []
    },
    // 导入预览计算属性
    validPreviewCount() {
      return this.previewRows.filter(r => r.errors.length === 0).length
    },
    invalidPreviewCount() {
      return this.previewRows.filter(r => r.errors.length > 0).length
    },
    duplicateCount() {
      return this.previewRows.filter(r => r.errors.includes('duplicate')).length
    },
    canConfirmImport() {
      return this.previewRows.length > 0 && this.validPreviewCount > 0
    }
  },
  methods: {
    riskText(score) {
      const t = this.$store.state.settings.settings.thresholds || { lowMax: 4, mediumMax: 10 }
      if (score > t.mediumMax) return '高'
      if (score > t.lowMax) return '中'
      return '低'
    },
    isSupervisor(row) {
      // 优先使用显式设置的 role / identity 字段
      if (row.role === 'supervisor' || row.identity === 'manager') return true
      if (row.role === 'employee' || row.identity === 'employee') return false
      // 未设置时按汇报关系自动判断（从全部员工中查找）
      return this.allList.some(e => e.manager_email === row.email)
    },
    getManagerName(email) {
      if (!email) return ''
      // 在全量员工中查找上级姓名（主管可能跨任务），不受当前 task 过滤影响
      const allEmployees = this.$store.state.employees.employees || []
      const manager = allEmployees.find(e => e.email === email)
      return manager ? manager.real_name : ''
    },
    getUserStatus(email) {
      const user = this.managers.find(u => u.email === email)
      return user ? (user.status === 1 ? 'normal' : 'disabled') : null
    },
    findManagerAccount(email) {
      if (!email) return null
      return this.managers.find(user => user.email === email) || null
    },
    toManagerResetItem(user) {
      return {
        user_id: user.user_id,
        real_name: user.real_name || user.email,
        email: user.email,
        status: user.status
      }
    },
    getSelectedManagerAccounts() {
      const selectedByEmail = {}
      this.selectedRows.forEach(row => {
        const account = this.findManagerAccount(row.email) || this.findManagerAccount(row.manager_email)
        if (account && !selectedByEmail[account.email]) {
          selectedByEmail[account.email] = this.toManagerResetItem(account)
        }
      })
      return Object.values(selectedByEmail)
    },
    async loadManagers() {
      if (!this.isSuperAdmin) return
      try {
        const res = await this.$api.fetchManagers()
        this.managers = res.data || []
      } catch (e) {
        console.warn('加载主管列表失败:', e)
      }
    },
    getSubordinateCount(email) {
      return this.$store.state.employees.employees.filter(e => e.manager_email === email).length
    },
    openAdd() {
      this.isEdit = false
      this.form = { employee_id: null, real_name: '', email: '', dept_name: '', job_title: '', manager_email: '' }
      this.dialogVisible = true
      this.$nextTick(() => this.$refs.formRef && this.$refs.formRef.clearValidate())
    },
    openEdit(row) {
      this.isEdit = true
      this.form = {
        employee_id: row.employee_id,
        real_name: row.real_name,
        email: row.email,
        dept_name: row.dept_name || '',
        job_title: row.job_title || '',
        manager_email: row.manager_email || ''
      }
      this.dialogVisible = true
      this.$nextTick(() => this.$refs.formRef && this.$refs.formRef.clearValidate())
    },
    async save() {
      const valid = await this.$refs.formRef.validate().catch(() => false)
      if (!valid) return
      // 邮箱唯一性校验
      const exists = this.allList.find(e => e.email === this.form.email && e.employee_id !== this.form.employee_id)
      if (exists) {
        this.$message.error('该邮箱已存在')
        return
      }
      this.saving = true
      try {
        if (this.isEdit) {
          await this.$api.updateEmployee(this.form.employee_id, {
            real_name: this.form.real_name,
            email: this.form.email,
            dept_name: this.form.dept_name,
            job_title: this.form.job_title,
            manager_email: this.form.manager_email
          })
          const isResigned = this.$store.state.employees.resignedEmployees.some(e => e.employee_id === this.form.employee_id)
          if (isResigned) {
            this.$store.commit('UPDATE_RESIGNED', { ...this.form })
          } else {
            this.$store.commit('UPDATE_EMPLOYEE', { ...this.form })
          }
          this.$message.success('已更新')
        } else {
          const res = await this.$api.createEmployee({
            real_name: this.form.real_name,
            email: this.form.email,
            dept_name: this.form.dept_name || '',
            job_title: this.form.job_title || '',
            manager_email: this.form.manager_email || '',
            employment_status: 1,
            task_id: this.$store.getters.currentTaskId || null
          })
          this.$store.commit('ADD_EMPLOYEE', {
            employee_id: res.data.employee_id,
            real_name: this.form.real_name,
            email: this.form.email,
            dept_name: this.form.dept_name || '',
            job_title: this.form.job_title || '',
            manager_email: this.form.manager_email || '',
            latest_score: 0,
            latest_risk_level: 1,
            employment_status: 1,
            task_id: this.$store.getters.currentTaskId || null
          })
          this.$message.success('已新增')
        }
        this.dialogVisible = false
      } catch (e) {
        this.$message.error('保存失败: ' + ((e.response && e.response.data && e.response.data.message) || e.message))
      } finally {
        this.saving = false
      }
    },
    // 分页
    handleSizeChange(size) {
      this.pageSize = size
      this.page = 1
    },
    handlePageChange(page) {
      this.page = page
    },
    // 删除保护：判断员工是否有评估记录
    hasEvalRecord(row) {
      const evals = this.$store.state.evaluations || []
      return evals.some(e => e.employee_id === row.employee_id)
    },
    async remove(row) {
      if (row.employment_status === 1 && this.hasEvalRecord(row)) {
        this.$message.warning('该员工已有评估记录，请先使用【标记离职】功能')
        return
      }
      try {
        await this.$confirm('确定删除 ' + row.real_name + '？', '提示', { type: 'warning' })
        await this.$api.deleteEmployee(row.employee_id)
        if (row.employment_status === 1) {
          this.$store.commit('DELETE_EMPLOYEE', row.employee_id)
        } else {
          this.$store.commit('DELETE_RESIGNED', row.employee_id)
        }
        this.$message.success('已删除')
      } catch (e) {
        if (e !== 'cancel') {
          this.$message.error('删除失败: ' + ((e.response && e.response.data && e.response.data.message) || e.message))
        }
      }
    },
    clearFilters() {
      this.filterDept = ''
      this.search = ''
      this.page = 1
    },
    // 恢复在职
    async restoreEmployee(row) {
      try {
        await this.$confirm('恢复在职将清空该员工的离职相关信息，是否继续？', '提示', { type: 'warning' })
        await this.$api.restoreEmployee(row.employee_id)
        this.$store.commit('RESTORE_EMPLOYEE', row.employee_id)
        this.$message.success('已恢复在职')
      } catch (e) {
        if (e !== 'cancel') {
          this.$message.error('恢复失败: ' + ((e.response && e.response.data && e.response.data.message) || e.message))
        }
      }
    },
    // 批量操作
    handleSelectionChange(selection) {
      this.selectedRows = selection
    },
    clearSelection() {
      this.selectedRows = []
      this.$refs.tableRef && this.$refs.tableRef.clearSelection()
    },
    batchResign() {
      if (!this.selectedRows.length) return
      this.resignForm = {
        employee_id: null,
        employee_name: '',
        resignation_date: new Date().toISOString().slice(0, 10),
        resignation_reasons: [],
        other_reason: ''
      }
      this.resignVisible = true
    },
    async batchDelete() {
      if (!this.selectedRows.length) return
      const withEval = this.selectedRows.filter(r => r.employment_status === 1 && this.hasEvalRecord(r))
      const deletable = this.selectedRows.filter(r => !(r.employment_status === 1 && this.hasEvalRecord(r)))
      if (withEval.length) {
        this.$message.warning(`${withEval.length} 人有评估记录不可删除，仅删除其余 ${deletable.length} 人`)
      }
      if (!deletable.length) return
      try {
        await this.$confirm(`确定删除选中的 ${deletable.length} 名员工？`, '提示', { type: 'warning' })
        for (const r of deletable) {
          await this.$api.deleteEmployee(r.employee_id)
          if (r.employment_status === 1) {
            this.$store.commit('DELETE_EMPLOYEE', r.employee_id)
          } else {
            this.$store.commit('DELETE_RESIGNED', r.employee_id)
          }
        }
        this.$message.success(`已删除 ${deletable.length} 人`)
        this.selectedRows = []
      } catch (e) {
        if (e !== 'cancel') {
          this.$message.error('批量删除失败: ' + ((e.response && e.response.data && e.response.data.message) || e.message))
        }
      }
    },
    // 标记离职弹窗
    openResignDialog(row) {
      this.resignForm = {
        employee_id: row.employee_id,
        employee_name: row.real_name,
        resignation_date: new Date().toISOString().slice(0, 10),
        resignation_reasons: [],
        other_reason: ''
      }
      this.resignVisible = true
    },
    async confirmResign() {
      this.saving = true
      try {
        const row = this.$store.state.employees.employees.find(e => e.employee_id === this.resignForm.employee_id)
        if (!row) {
          // 批量标记离职：只处理在职员工
          const activeRows = this.selectedRows.filter(emp => emp.employment_status === 1)
          if (activeRows.length) {
            for (const emp of activeRows) {
              await this.doMarkResigned(emp)
            }
            this.$message.success(`已批量标记 ${activeRows.length} 人离职`)
          }
          if (this.selectedRows.length !== activeRows.length) {
            this.$message.warning(`${this.selectedRows.length - activeRows.length} 人已离职，跳过`)
          }
          this.selectedRows = []
          this.resignVisible = false
          this.saving = false
          return
        }
        await this.doMarkResigned(row)
        this.resignVisible = false
        this.$message.success('已标记离职，并生成离职反馈记录')
      } catch (e) {
        this.$message.error('标记离职失败: ' + ((e.response && e.response.data && e.response.data.message) || e.message))
      } finally {
        this.saving = false
      }
    },
    async doMarkResigned(emp) {
      const reasons = [...this.resignForm.resignation_reasons]
      if (reasons.includes('其他') && this.resignForm.other_reason) {
        const idx = reasons.indexOf('其他')
        reasons[idx] = '其他：' + this.resignForm.other_reason
      }
      const reasonStr = reasons.join('、')
      const user = this.$store.state.user || {}
      await this.$api.markResigned(emp.employee_id, {
        resignation_date: this.resignForm.resignation_date,
        resignation_reason: reasonStr
      })
      // 1. 标记离职
      this.$store.commit('MARK_RESIGNED', {
        id: emp.employee_id,
        date: this.resignForm.resignation_date,
        reason: reasonStr,
        manager_email: user.email || ''
      })
      // 2. 自动创建离职反馈记录
      this.$store.commit('ADD_FEEDBACK', {
        feedback_id: Date.now() + Math.floor(Math.random() * 1000),
        employee_id: emp.employee_id,
        employee_name: emp.real_name,
        resignation_date: this.resignForm.resignation_date,
        resignation_reason: reasonStr,
        manager_email: user.email || '',
        source: 'auto',
        created_at: new Date().toISOString().replace('T', ' ').slice(0, 19)
      })
    },
    // 下载 Excel 模板
    downloadTemplate() {
      const headers = ['姓名', '邮箱', '部门', '职位', '上级邮箱']
      const sample = [['张三', 'zhangsan@company.com', '技术中心', '开发工程师', 'manager@company.com']]
      const ws = XLSX.utils.aoa_to_sheet([headers, ...sample])
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, '员工模板')

      // 填写说明 sheet
      const descHeaders = ['字段名', '是否必填', '填写规则']
      const descData = [
        ['姓名', '必填', '员工真实姓名'],
        ['邮箱', '必填', '企业邮箱，全系统唯一，将作为登录账号'],
        ['部门', '可选', '所属部门名称，纯文本'],
        ['职位', '可选', '职位名称'],
        ['上级邮箱', '必填', '汇报上级的企业邮箱']
      ]
      const descWs = XLSX.utils.aoa_to_sheet([descHeaders, ...descData])
      XLSX.utils.book_append_sheet(wb, descWs, '填写说明')
      console.log('[downloadTemplate] sheets:', wb.SheetNames)

      XLSX.writeFile(wb, '员工导入模板.xlsx')
    },
    // 导出全部员工（不受筛选影响）
    exportEmployees() {
      const behaviors = this.$store.state.behaviors.behaviors || []
      const data = this.allList.map(e => {
        // 行为ID转描述文本
        let behaviorLabels = ''
        if (e.observed_behaviors && e.observed_behaviors.length) {
          behaviorLabels = e.observed_behaviors.map(id => {
            const b = behaviors.find(x => x.behavior_id === id)
            return b ? b.description : id
          }).join('、')
        }
        return {
          系统ID: e.employee_id,
          姓名: e.real_name,
          邮箱: e.email,
          部门: e.dept_name || '',
          职位: e.job_title || '',
          上级姓名: e.manager_name || this.getManagerName(e.manager_email) || '',
          汇报上级邮箱: e.manager_email || '',
          在职状态: e.employment_status === 1 ? '在职' : '已离职',
          最新评估分数: e.latest_score,
          最新风险等级: this.riskText(e.latest_score),
          离职日期: e.resignation_date || '',
          离职原因: e.resignation_reason || '',
          观察到的行为: behaviorLabels,
          行为详情: e.behavior_details || '',
          主管建议: e.suggestions || ''
        }
      })
      const ws = XLSX.utils.json_to_sheet(data)
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, '员工列表')
      XLSX.writeFile(wb, `员工列表_${new Date().toISOString().slice(0, 10)}.xlsx`)
      this.$message.success(`已导出 ${data.length} 条员工数据`)
    },
    // Excel 导入（闭环流程：上传 → 预览 → 确认 → 写入）
    openImport() {
      this.importVisible = true
      this.importFile = null
      this.importResult = { summary: '', errors: [], success: 0, fail: 0 }
      this.previewRows = []
      this.previewErrors = []
      this.importStrategy = 'skip'
      this.$nextTick(() => {
        if (this.$refs.uploadRef) this.$refs.uploadRef.clearFiles()
      })
    },
    handleFileChange(file) {
      this.importFile = file.raw
      this.previewRows = []
      this.previewErrors = []
      // 上传后立即解析预览，不写入 store
      if (this.importFile) {
        const reader = new FileReader()
        reader.onload = (e) => {
          try {
            const data = new Uint8Array(e.target.result)
            const workbook = XLSX.read(data, { type: 'array' })
            const firstSheet = workbook.Sheets[workbook.SheetNames[0]]
            const jsonData = XLSX.utils.sheet_to_json(firstSheet, { header: 1 })
            this.buildPreview(jsonData)
          } catch (err) {
            this.$message.error('文件解析失败：' + err.message)
          }
        }
        reader.readAsArrayBuffer(this.importFile)
      }
    },
    // 构建预览数据（不写入 store）
    buildPreview(rows) {
      if (!rows || rows.length < 2) {
        this.$message.warning('文件为空或格式不正确')
        return
      }
      const headers = rows[0].map(h => String(h).trim())
      const nameIdx = headers.findIndex(h => h.includes('姓名'))
      const emailIdx = headers.findIndex(h => h.includes('邮箱'))
      const deptIdx = headers.findIndex(h => h.includes('部门'))
      const jobIdx = headers.findIndex(h => h.includes('职位'))
      const managerIdx = headers.findIndex(h => h.includes('上级邮箱'))

      if (nameIdx === -1 || emailIdx === -1) {
        this.$message.error('文件缺少必要的列：姓名、邮箱')
        return
      }

      const preview = []
      const errors = []
      const existingEmails = new Set(this.allList.map(e => e.email))

      for (let i = 1; i < rows.length; i++) {
        const row = rows[i]
        const realName = String(row[nameIdx] || '').trim()
        const email = String(row[emailIdx] || '').trim()

        if (!realName && !email) continue // 跳过空行

        const rowErrors = []
        if (!realName) rowErrors.push('name')
        if (!email) rowErrors.push('email')
        else if (!EMAIL_REGEX.test(email)) rowErrors.push('email')
        else if (existingEmails.has(email)) rowErrors.push('duplicate')

        preview.push({
          _rowIndex: i + 1,
          real_name: realName,
          email: email,
          dept_name: deptIdx >= 0 ? String(row[deptIdx] || '').trim() : '',
          job_title: jobIdx >= 0 ? String(row[jobIdx] || '').trim() : '',
          manager_email: managerIdx >= 0 ? String(row[managerIdx] || '').trim() : '',
          errors: rowErrors
        })

        if (rowErrors.includes('name')) errors.push(`第${i + 1}行：姓名不能为空`)
        if (rowErrors.includes('email') && email) errors.push(`第${i + 1}行（${realName}）：邮箱格式不正确`)
        if (rowErrors.includes('email') && !email) errors.push(`第${i + 1}行（${realName}）：邮箱不能为空`)
        if (rowErrors.includes('duplicate')) errors.push(`第${i + 1}行（${realName}）：邮箱 ${email} 已存在`)
      }

      this.previewRows = preview
      this.previewErrors = errors
      this.$message.info(`解析完成：共 ${preview.length} 行，有效 ${preview.filter(r => r.errors.length === 0).length} 行`)
    },
    // 确认导入：先调API，成功后写入 store
    async confirmImport() {
      if (!this.previewRows.length || this.importing) return
      this.importing = true
      let success = 0
      let skipped = 0
      let fail = 0
      const existingByEmail = {}
      this.allList.forEach(e => { existingByEmail[e.email] = e })

      for (const row of this.previewRows) {
        if (row.errors.length > 0 && !row.errors.includes('duplicate')) {
          fail++
          continue
        }

        if (row.errors.includes('duplicate')) {
          if (this.importStrategy === 'skip') {
            skipped++
            continue
          }
          const exist = existingByEmail[row.email]
          if (exist) {
            try {
              await this.$api.updateEmployee(exist.employee_id, {
                real_name: row.real_name || exist.real_name,
                dept_name: row.dept_name !== undefined ? row.dept_name : exist.dept_name,
                job_title: row.job_title !== undefined ? row.job_title : exist.job_title,
                manager_email: row.manager_email !== undefined ? row.manager_email : exist.manager_email
              })
              this.$store.commit('UPDATE_EMPLOYEE', {
                ...exist,
                real_name: row.real_name || exist.real_name,
                dept_name: row.dept_name !== undefined ? row.dept_name : exist.dept_name,
                job_title: row.job_title !== undefined ? row.job_title : exist.job_title,
                manager_email: row.manager_email !== undefined ? row.manager_email : exist.manager_email
              })
              success++
            } catch (e) {
              fail++
              console.error('导入更新失败:', row.email, e)
            }
            continue
          }
        }

        try {
          const res = await this.$api.createEmployee({
            real_name: row.real_name,
            email: row.email,
            dept_name: row.dept_name || '',
            job_title: row.job_title || '',
            manager_email: row.manager_email || '',
            employment_status: 1,
            task_id: this.$store.getters.currentTaskId || null
          })
          this.$store.commit('ADD_EMPLOYEE', {
            employee_id: res.data.employee_id,
            real_name: row.real_name,
            email: row.email,
            dept_name: row.dept_name || '',
            job_title: row.job_title || '',
            manager_email: row.manager_email || '',
            latest_score: 0,
            latest_risk_level: 1,
            employment_status: 1,
            task_id: this.$store.getters.currentTaskId || null
          })
          success++
        } catch (e) {
          fail++
          console.error('导入创建失败:', row.email, e)
        }
      }

      const msg = `导入完成：成功 ${success} 条，跳过重复 ${skipped} 条，失败 ${fail} 条`
      this.importResult = { summary: msg, errors: this.previewErrors, success, fail }
      this.$message.success(msg)
      this.previewRows = []
      this.importFile = null
      this.$nextTick(() => {
        if (this.$refs.uploadRef) this.$refs.uploadRef.clearFiles()
      })
      this.importing = false
    },
    // 主管账号操作
    async openResetPassword() {
      if (!this.managers.length) {
        await this.loadManagers()
      }
      const managers = this.getSelectedManagerAccounts()
      if (!managers.length) {
        this.$message.warning('请先选择主管账号')
        return
      }
      this.resetPwdList = managers
      this.resetPwdVisible = true
    },
    async confirmResetPassword() {
      let count = 0
      for (const emp of this.resetPwdList) {
        try {
          await this.$api.resetManagerPassword(emp.email)
          count++
        } catch (e) {
          this.$message.error(`重置 ${emp.email} 失败: ${e.message || ''}`)
        }
      }
      this.$message.success(`已重置 ${count} 位主管密码`)
      this.loadManagers()
      this.resetPwdVisible = false
      this.resetPwdList = []
      this.clearSelection()
    },
    openDisable() {
      const managers = this.selectedRows.filter(r => this.isSupervisor(r) && this.getUserStatus(r.email) === 'normal')
      if (!managers.length) {
        this.$message.warning('请先选择正常状态的主管账号')
        return
      }
      this.disableList = managers
      this.disableVisible = true
    },
    async confirmDisable() {
      let count = 0
      for (const emp of this.disableList) {
        try {
          await this.$api.disableManager(emp.email)
          count++
        } catch (e) {
          this.$message.error(`禁用 ${emp.email} 失败: ${e.message || ''}`)
        }
      }
      this.$message.success(`已禁用 ${count} 位主管账号`)
      this.loadManagers()
      this.disableVisible = false
      this.disableList = []
      this.clearSelection()
    },
    openEnable() {
      const managers = this.selectedRows.filter(r => this.isSupervisor(r) && this.getUserStatus(r.email) === 'disabled')
      if (!managers.length) {
        this.$message.warning('请先选择已禁用的主管账号')
        return
      }
      this.enableList = managers
      this.enableVisible = true
    },
    async confirmEnable() {
      let count = 0
      for (const emp of this.enableList) {
        try {
          await this.$api.enableManager(emp.email)
          count++
        } catch (e) {
          this.$message.error(`启用 ${emp.email} 失败: ${e.message || ''}`)
        }
      }
      this.$message.success(`已启用 ${count} 位主管账号`)
      this.loadManagers()
      this.enableVisible = false
      this.enableList = []
      this.clearSelection()
    },
    copyText(text) {
      if (navigator.clipboard) {
        navigator.clipboard.writeText(text).then(() => {
          this.$message.success('密码已复制')
        }).catch(() => {
          this.$message.warning('复制失败，请手动复制')
        })
      } else {
        const input = document.createElement('input')
        input.value = text
        document.body.appendChild(input)
        input.select()
        document.execCommand('copy')
        document.body.removeChild(input)
        this.$message.success('密码已复制')
      }
    },
    openAddResignedDialog() {
      this.resignedForm = {
        real_name: '',
        email: '',
        dept_name: '',
        job_title: '',
        resignation_date: new Date().toISOString().slice(0, 10),
        resignation_reason: ''
      }
      this.addResignedDialogVisible = true
      this.$nextTick(() => this.$refs.resignedFormRef && this.$refs.resignedFormRef.clearValidate())
    },
    async confirmAddResigned() {
      const valid = await this.$refs.resignedFormRef.validate().catch(() => false)
      if (!valid) return
      const exists = this.allList.find(e => e.email === this.resignedForm.email)
      if (exists) {
        this.$message.error('该邮箱已存在')
        return
      }
      this.saving = true
      try {
        const empId = Date.now()
        const emp = {
          employee_id: empId,
          employee_no: 'L' + empId,
          real_name: this.resignedForm.real_name.trim(),
          email: this.resignedForm.email.trim(),
          dept_name: this.resignedForm.dept_name.trim() || '',
          job_title: this.resignedForm.job_title.trim() || '',
          resignation_date: this.resignedForm.resignation_date,
          resignation_reason: this.resignedForm.resignation_reason.trim() || '',
          employment_status: 2,
          identity: 'employee',
          latest_score: 0,
          latest_risk_level: null,
          manager_email: '',
          task_id: this.$store.getters.currentTaskId || null
        }
        this.$store.commit('ADD_RESIGNED', emp)
        this.$message.success('离职员工已添加')
        this.addResignedDialogVisible = false
      } finally {
        this.saving = false
      }
    }
  }
}
</script>

<style scoped>
.toolbar { display: flex; align-items: center; margin-bottom: var(--space-lg); }
.table-wrapper { background: var(--color-surface); border: 1px solid var(--color-border); border-radius: var(--radius-lg); overflow: hidden; box-shadow: var(--shadow-sm); }
.data-table >>> th { background: var(--color-bg) !important; color: var(--color-text) !important; font-weight: 600 !important; }
.risk-dot { display: inline-block; width: 8px; height: 8px; border-radius: var(--radius-full); margin-right: 4px; }
.risk-dot-1 { background: var(--color-low); }
.risk-dot-2 { background: var(--color-medium); }
.risk-dot-3 { background: var(--color-high); }
.action-edit { color: var(--color-primary) !important; }
.action-disable { color: var(--color-high) !important; }
.action-disable-locked { color: var(--color-text-muted) !important; cursor: not-allowed; }

.import-area { padding: 0 10px; }
.import-header { display: flex; align-items: flex-start; justify-content: space-between; margin-bottom: 16px; }
.import-tip { font-size: 13px; color: var(--color-text-secondary); line-height: 1.6; margin: 0; }
.upload-box >>> .el-upload { width: 100%; }
.upload-box >>> .el-upload-dragger { width: 100%; }
.pagination-bar { display: flex; justify-content: flex-end; padding: var(--space-md) var(--space-lg); background: var(--color-surface); border-top: 1px solid var(--color-border); }
.batch-bar { display: flex; align-items: center; justify-content: space-between; padding: var(--space-md) var(--space-lg); background: var(--color-bg); border-top: 1px solid var(--color-border); }
.batch-info { font-size: 13px; color: var(--color-text-secondary); }
.batch-actions { display: flex; gap: var(--space-sm); }
.import-preview { margin-top: 10px; }
.preview-header { margin-bottom: 10px; }
.preview-header p { font-size: 13px; color: var(--color-text-secondary); margin: 0 0 8px 0; }
.duplicate-strategy { display: flex; align-items: center; gap: var(--space-sm); flex-wrap: wrap; }
.preview-table >>> .el-table__row .text-error { color: var(--color-high); font-weight: 600; }
.preview-errors ul { max-height: 120px; overflow-y: auto; margin: 8px 0; padding-left: 20px; color: var(--color-high); font-size: 13px; }
.preview-errors li { margin-bottom: 4px; }
</style>
